import React, { useState } from 'react';
import Toggle from '../../components/common/Toggle';
import ConfirmDialog from '../../components/common/WarningBox';
import { BarLoader } from 'react-spinners';

interface Report {
    customer: string;
    last_sent?: string;
    report_status?: 'success' | 'fail' | null | undefined;
}

interface ReportButtonProps {
    reportName: string;
    onTrigger: (mailingList: string, currentMonth: boolean, selectedMonth: string) => void;
    reportData?: Report;
    isLoading?: boolean;
    hasAdminAccess?: boolean;
}

const ReportButton: React.FC<ReportButtonProps> = ({ 
    reportName, 
    onTrigger, 
    reportData, 
    isLoading = false,
    hasAdminAccess = false
}) => {
    const [sendToCustomer, setSendToCustomer] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(true);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const handleTrigger = async () => {
        try {
            const mailingList = sendToCustomer ? 'customer' : 'user';

            // Show confirmation dialog only when sending to customer
            if (sendToCustomer) {
                setShowConfirmDialog(true);
            } else {
                // Send directly if sending to user
                onTrigger(mailingList, currentMonth, selectedMonth);
            }
        } catch (error) {
            console.error(`Error triggering ${reportName} report:`, error);
        }
    }
    
    const handleConfirmSend = () => {
        const mailingList = sendToCustomer ? 'customer' : 'user';
        onTrigger(mailingList, currentMonth, selectedMonth);
        setShowConfirmDialog(false);
    };

    const handleCancelSend = () => {
        setShowConfirmDialog(false);
    };

    // Generate month/year options from December 2012 to today (most recent first)
    const generateMonthOptions = () => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0-based
        
        const options = [];
        
        // Start from current month/year and go backwards
        for (let year = currentYear; year >= 2012; year--) {
            const endMonth = year === currentYear ? currentMonth : 11;
            const startMonth = year === 2012 ? 11 : 0; // Start from December 2012
            
            for (let month = endMonth; month >= startMonth; month--) {
                options.push(`${months[month]} ${year}`);
            }
        }
        
        return options;
    };

    const monthOptions = generateMonthOptions();
    const getConfirmationMessage = () => {
        if (!currentMonth) {
            return (
                <>
                    This report will be sent to {reportName} for <strong>{selectedMonth}</strong>. Are you sure you want to proceed?
                </>
            );
        }
        return `This report will be sent to ${reportName}. Are you sure you want to proceed?`;
    };
    
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);

    // Function to determine text color based on last sent date
    const getTextColorClass = (lastSentStr: string | undefined, status: 'success' | 'fail' | null | undefined) => {
        // If report failed, always use red
        if (status === 'fail') return 'text-red-600';
        
        // If report succeeded, check the date
        if (status === 'success') {
            if (!lastSentStr) return 'text-green-600';
            
            const lastSent = new Date(lastSentStr);
            const today = new Date();
            
            // Reset hours to compare just the date
            today.setHours(0, 0, 0, 0);
            const lastSentDate = new Date(lastSent);
            lastSentDate.setHours(0, 0, 0, 0);
            
            // If last sent is today, use green
            if (lastSentDate.getTime() === today.getTime()) {
                return 'text-green-600';
            }
            
            // If last sent is not today (yesterday or earlier), use yellow
            return 'text-yellow-600';
        }
        
        // Default gray for no status
        return 'text-gray-600';
    };
    return (
        <>
            <div className="triggers-buttonContainer">
                <button
                    onClick={handleTrigger}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                >
                    {reportName} Report
                </button>
                {hasAdminAccess && (
                    <Toggle 
                        checked={sendToCustomer} 
                        onChange={setSendToCustomer}
                        labelOn={'Send to customer'}
                        labelOff={'Send to me'}
                    />
                )}
                <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id={`currentMonth-${reportName}`}
                            checked={currentMonth}
                            onChange={(e) => setCurrentMonth(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label 
                            htmlFor={`currentMonth-${reportName}`}
                            className="ml-2 text-sm font-medium text-gray-700"
                        >
                            Current Month
                        </label>
                    </div>
                    
                    {!currentMonth && (
                        <div className="flex items-center">
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {monthOptions.map((monthYear) => (
                                    <option key={monthYear} value={monthYear}>
                                        {monthYear}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {reportData ? (
                        <div className="ml-4 text-sm flex items-center">
                            <span className={getTextColorClass(reportData?.last_sent, reportData?.report_status)}>
                                Last sent: {reportData?.last_sent ? new Date(reportData.last_sent).toLocaleString() : 'Never'}
                            </span>
                        </div>
                    ) : (
                        <div className="ml-4 text-sm flex items-center">
                            <span className="ml-2 text-sm font-medium text-gray-700">
                                {'Loading...'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDialog
                isOpen={showConfirmDialog}
                onClose={handleCancelSend}
                onConfirm={handleConfirmSend}
                title="Confirm Customer Report"
                message={getConfirmationMessage()}
                button1="Cancel"
                button2="Send Report"
                button2Color="green"
                showbutton2={true}
                showTextInput={false}
            />
        </>
    );
};

export default ReportButton;